import { createPortal } from 'react-dom'
import { AnimatePresence, motion } from 'framer-motion'
import OpenInNewRoundedIcon from '@mui/icons-material/OpenInNewRounded'
import type { HelperStoreSnapshot } from '../../services/helper/helperStore'
import { usePushBackHandler } from '../../context/BackStackContext'
import { buildInfo } from '../../utils/buildInfo'
import { Pressable } from '../Common'
import { useNls } from '../../nls/nlsProvider'
import type { NlsKey } from '../../nls/strings_nls'

const MotionPressable = motion.create(Pressable)

type Item = {
  label: string
  description?: string
  onClick: () => void
  destructive?: boolean
}

export type HelperMenuDrawerProps = {
  open: boolean
  onClose: () => void
  displayName: string | null
  identity: string | null
  avatarUrl: string | null
  snapshot: HelperStoreSnapshot
  onOpenSettings: () => void
  onOpenProfile: () => void
  onOpenIncomingInvites: () => void
  onEnterFocus: () => void
  focusModeActive: boolean
  onOpenOverview?: () => void
  onOpenForms?: () => void
  onSignOut: () => void
}

type PillLabel = 'READY' | 'QUEUED' | 'FOCUS' | 'OFFLINE'

const PILL_TONE: Record<PillLabel, string> = {
  READY: 'text-[var(--success)]', // mosaic-keep: dot
  QUEUED: 'text-[var(--warning)]', // mosaic-keep: dot
  FOCUS: 'text-[var(--accent)]', // mosaic-keep: dot
  OFFLINE: 'text-[var(--danger)]', // mosaic-keep: dot
}

const PILL_TEXT_TONE: Record<PillLabel, string> = {
  READY: 'text-[color-mix(in_srgb,var(--success)_60%,var(--text-strong))]',
  QUEUED: 'text-[color-mix(in_srgb,var(--warning)_60%,var(--text-strong))]',
  FOCUS: 'text-[color-mix(in_srgb,var(--accent)_60%,var(--text-strong))]',
  OFFLINE: 'text-[color-mix(in_srgb,var(--danger)_60%,var(--text-strong))]',
}

const PILL_STATUS_KEY: Record<PillLabel, NlsKey> = {
  READY: 'NLSHelperMenuStatusReady',
  QUEUED: 'NLSHelperMenuStatusQueued',
  OFFLINE: 'NLSHelperMenuStatusOffline',
  FOCUS: 'NLSHelperMenuStatusFocus',
}

const formatBuiltAt = (iso: string): string => new Date(iso).toISOString().slice(0, 10)

export const HelperMenuDrawer = ({
  open,
  onClose,
  displayName,
  identity,
  avatarUrl,
  snapshot,
  onOpenSettings,
  onOpenProfile,
  onOpenIncomingInvites,
  onEnterFocus,
  focusModeActive,
  onOpenOverview,
  onOpenForms,
  onSignOut,
}: HelperMenuDrawerProps) => {
  usePushBackHandler(onClose, open)
  const { t } = useNls()
  const pillLabel: PillLabel = focusModeActive
    ? 'FOCUS'
    : snapshot.syncState === 'OFFLINE' || snapshot.syncState === 'ERROR'
      ? 'OFFLINE'
      : snapshot.pending.length > 0
        ? 'QUEUED'
        : 'READY'
  const items: Item[] = [
    ...(onOpenOverview
      ? [{
          label: t('NLSHelperMenuOverview'),
          description: t('NLSHelperMenuOverviewDesc'),
          onClick: () => {
            onOpenOverview()
            onClose()
          },
        }]
      : []),
    ...(onOpenForms
      ? [{
          label: t('NLSHelperFormsTitle'),
          description: t('NLSHelperMenuFormsDesc'),
          onClick: () => {
            onOpenForms()
            onClose()
          },
        }]
      : []),
    {
      label: t('NLSHelperMenuFocus'),
      description: t('NLSHelperMenuFocusDesc'),
      onClick: () => {
        onEnterFocus()
        onClose()
      },
    },
    {
      label: t('NLSHelperMenuProfile'),
      description: t('NLSHelperMenuProfileDesc'),
      onClick: () => {
        onOpenProfile()
        onClose()
      },
    },
    {
      label: t('NLSHelperIncomingInvites'),
      description: t('NLSHelperMenuIncomingInvitesDesc'),
      onClick: () => {
        onOpenIncomingInvites()
        onClose()
      },
    },
    {
      label: t('NLSSideMenuSettings'),
      description: t('NLSHelperMenuSettingsDesc'),
      onClick: () => {
        onOpenSettings()
        onClose()
      },
    },
    {
      label: t('NLSHelperMenuHelp'),
      description: t('NLSHelperMenuHelpDesc'),
      onClick: () => {
        if (typeof window !== 'undefined') window.open('mailto:support@timelining.app', '_blank', 'noopener,noreferrer')
        onClose()
      },
    },
    {
      label: t('NLSHelperMenuAbout'),
      description: t('NLSHelperMenuAboutDesc'),
      onClick: () => {
        if (typeof window !== 'undefined') window.open('https://timelining.app/about', '_blank', 'noopener,noreferrer')
        onClose()
      },
    },
  ]

  if (typeof document === 'undefined') return null

  return createPortal(
    <AnimatePresence>
      {open ? (
        <>
          <MotionPressable
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-40 bg-[color-mix(in_srgb,var(--bg)_85%,transparent)]"
            onClick={onClose}
            aria-label={t('NLSHelperMenuCloseAria')}
          />
          {/* eslint-disable-next-line no-restricted-syntax -- helper side navigation drawer uses custom spring motion and is not a Common modal */}
          <motion.aside role="dialog"
            initial={{ x: '-100%' }}
            animate={{ x: 0 }}
            exit={{ x: '-100%' }}
            transition={{ type: 'spring', stiffness: 280, damping: 30 }}
            className="fixed top-0 bottom-0 left-0 z-50 w-[80vw] max-w-[340px] bg-[var(--surface)] border-r border-[var(--border)] shadow-2xl flex flex-col"
            aria-label={t('NLSHelperMenuDrawerAria')}
            data-testid="helper-menu-drawer"
            data-ui-name="UI-MPCAP-HMENU"
          >
            <header className="px-5 pt-6 pb-4 border-b border-[var(--border)]">
              <div className="flex items-center gap-3">
                <div className="h-12 w-12 rounded-2xl bg-[var(--surface-muted)] border border-[var(--border)] flex items-center justify-center overflow-hidden">
                  {avatarUrl ? (
                    <img src={avatarUrl} alt="" className="h-full w-full object-cover" />
                  ) : (
                    <span className="text-base font-semibold text-[var(--text-strong)]">
                      {(displayName ?? 'H').slice(0, 1).toUpperCase()}
                    </span>
                  )}
                </div>
                <div className="min-w-0">
                  <p className="text-sm font-semibold text-[var(--text-strong)] truncate">{displayName ?? 'Helper'}</p>
                  {identity ? <p className="text-xs text-[var(--muted)] truncate">{identity}</p> : null}
                </div>
              </div>
              <div className="mt-4 flex items-center gap-2 text-[11px] font-medium tracking-normal">
                <span className={`h-2 w-2 rounded-full bg-current ${PILL_TONE[pillLabel]}`} />
                <span className={PILL_TEXT_TONE[pillLabel]}>{t(PILL_STATUS_KEY[pillLabel])}</span>
                {pillLabel === 'QUEUED' && snapshot.pending.length > 0 ? (
                  <span className="text-[var(--muted)]">· {snapshot.pending.length} {t('NLSHelperMenuPendingSuffix')}</span>
                ) : null}
              </div>
            </header>
            <nav className="flex-1 overflow-y-auto py-2">
              {items.map((item) => (
                <Pressable
                  key={item.label}
                  onClick={item.onClick}
                  className={`w-full text-left px-5 py-3 flex flex-col gap-0.5 transition-colors ${item.destructive ? 'text-[color-mix(in_srgb,var(--danger)_60%,var(--text-strong))] hover:bg-[var(--danger-soft)]' : 'text-[var(--text-strong)] hover:bg-[var(--surface-hover)]'}`}
                >
                  <span className="flex items-center justify-between gap-3 text-sm font-medium">
                    <span>{item.label}</span>
                    {item.label === 'Help & support' || item.label === 'About Timelining' ? (
                      <OpenInNewRoundedIcon style={{ fontSize: 15 }} className="text-[var(--muted)]" aria-hidden="true" />
                    ) : null}
                  </span>
                  {item.description ? <span className="text-[11px] text-[var(--muted)]">{item.description}</span> : null}
                </Pressable>
              ))}
            </nav>
            <div className="border-t border-[var(--border)] py-1">
              <Pressable
                onClick={() => {
                  onSignOut()
                  onClose()
                }}
                className="w-full text-left px-5 py-3 flex items-center gap-3 text-sm font-medium text-[color-mix(in_srgb,var(--danger)_60%,var(--text-strong))] hover:bg-[var(--danger-soft)] transition-colors"
              >
                <span>{t('NLSHelperSignOut')}</span>
              </Pressable>
            </div>
            <footer className="px-5 py-3 text-[10px] text-[var(--muted)] border-t border-[var(--border)] flex flex-col gap-0.5">
              <span>{t('NLSHelperMenuFooterTitle')}</span>
              <span className="text-[var(--muted)] text-[10px]">
                build {buildInfo.version} · {formatBuiltAt(buildInfo.builtAt)}
              </span>
            </footer>
          </motion.aside>
        </>
      ) : null}
    </AnimatePresence>,
    document.body,
  )
}
